import { BookingService } from '@/services/booking.service';
import type { QueryClient } from '@tanstack/react-query';
import type { BookingDetailResponseDto, BookingListSection, ProfileBookingsResponseDto } from '@/types/dto/booking.dto';
import { queryOptions } from '@tanstack/react-query';

const BOOKING_QUERY_DEFAULTS = {
  retry: false,
  staleTime: 0,
  refetchOnWindowFocus: false,
} as const;

export const bookingQueryKeys = {
  all: ['booking'] as const,
  profileLists: (viewerId?: string | null) => [...bookingQueryKeys.all, 'profile', viewerId ?? null] as const,
  profile: (section: BookingListSection, viewerId?: string | null) =>
    [...bookingQueryKeys.profileLists(viewerId), section] as const,
  detail: (bookingId?: string | null) => [...bookingQueryKeys.all, 'detail', bookingId ?? null] as const,
  availability: (companyId?: string, serviceIds?: string[], professionalId?: string, cursor?: string | null) =>
    [
      ...bookingQueryKeys.all,
      'availability',
      companyId ?? null,
      serviceIds ? serviceIds.toSorted() : null,
      professionalId ?? null,
      cursor ?? null,
    ] as const,
};

export const bookingQueryOptions = {
  profile: (section: BookingListSection, viewerId?: string | null, enabled = true) =>
    queryOptions<ProfileBookingsResponseDto>({
      queryKey: bookingQueryKeys.profile(section, viewerId),
      enabled: enabled && Boolean(viewerId),
      queryFn: () => BookingService.list(section),
      ...BOOKING_QUERY_DEFAULTS,
    }),
  availability: (
    companyId?: string,
    serviceIds?: string[],
    professionalId?: string,
    cursor?: string | null,
    enabled = true,
  ) =>
    queryOptions({
      queryKey: bookingQueryKeys.availability(companyId, serviceIds, professionalId, cursor),
      enabled: enabled && Boolean(companyId) && Boolean(serviceIds?.length) && Boolean(professionalId),
      queryFn: async () => {
        if (!companyId) {
          throw new Error('Company ID is required');
        }

        if (!serviceIds?.length) {
          throw new Error('At least one service ID is required');
        }

        if (!professionalId) {
          throw new Error('Professional ID is required');
        }

        return BookingService.getAvailability(companyId, serviceIds, professionalId, cursor);
      },
      ...BOOKING_QUERY_DEFAULTS,
    }),
  detail: (bookingId?: string | null, enabled = true) =>
    queryOptions<BookingDetailResponseDto>({
      queryKey: bookingQueryKeys.detail(bookingId),
      enabled: enabled && Boolean(bookingId),
      queryFn: async () => {
        if (!bookingId) {
          throw new Error('Booking ID is required');
        }

        return BookingService.detail(bookingId);
      },
      ...BOOKING_QUERY_DEFAULTS,
    }),
};

export async function refreshProfileBookingsCache(queryClient: QueryClient, viewerId?: string | null) {
  const queryKey = bookingQueryKeys.profileLists(viewerId);

  await queryClient.invalidateQueries({ queryKey });
  await queryClient.refetchQueries({ queryKey, type: 'active' });
}

export async function refreshBookingDetailCache(queryClient: QueryClient, bookingId?: string | null) {
  const queryKey = bookingQueryKeys.detail(bookingId);

  await queryClient.invalidateQueries({ queryKey });
  await queryClient.refetchQueries({ queryKey, type: 'active' });
}
